/**
 * Navigation - The Intelligent Standard
 *
 * Clean, minimal navigation. Adapts to page context and scroll position.
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { logError } from '@/lib/logger';

import { getLayoutConfig } from '../config/layout';
import { useAuth } from '../contexts/AuthContext';
import { useIsMeridianUser } from '../contexts/FeatureFlagContext';
import { Logo } from './Logo';

export function Navigation() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [scrollProgress, setScrollProgress] = useState(0);
  const isMeridianUser = useIsMeridianUser();
  // Track scroll position relative to hero height
  useEffect(() => {
    const handleScroll = () => {
      // Hero is roughly 85% of viewport height on these pages
      const heroHeight = window.innerHeight * 0.85;
      // Start transitioning when 60% through the hero, complete by end
      const transitionStart = heroHeight * 0.6;
      const transitionEnd = heroHeight;

      const progress = Math.min(
        Math.max(
          (window.scrollY - transitionStart) /
            (transitionEnd - transitionStart),
          0
        ),
        1
      );
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset scroll state on route change
  useEffect(() => {
    // Re-check scroll position after route transition settles
    const timer = setTimeout(() => {
      const heroHeight = window.innerHeight * 0.85;
      const transitionStart = heroHeight * 0.6;
      const transitionEnd = heroHeight;
      // Calculate fresh progress - will be 0 if at top of page
      const progress = Math.min(
        Math.max(
          (window.scrollY - transitionStart) /
            (transitionEnd - transitionStart),
          0
        ),
        1
      );
      setScrollProgress(progress);
    }, 50);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      logError('Sign out error', error);
    }
  };

  // Get layout configuration for current route
  const layout = getLayoutConfig(location.pathname);

  // Calculate opacity/color values based on scroll progress
  // Dark hero pages transition from transparent→opaque, others stay opaque
  const progress = layout.darkHero ? scrollProgress : 1;

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Interpolate colors based on progress
  const getLinkColor = (path: string) => {
    if (progress < 0.5) {
      // Still in dark mode
      return isActive(path)
        ? 'var(--color-white)'
        : 'rgba(var(--rgb-white), 0.7)';
    } else {
      // Transitioning to light mode
      return isActive(path) ? 'var(--color-meridian)' : 'var(--color-gray)';
    }
  };

  const linkStyle = (path: string) => ({
    color: getLinkColor(path),
    transition: 'color 0.2s ease',
  });

  return (
    <>
      <style>{`
        .nav-link {
          position: relative;
        }
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 2px;
          left: 50%;
          width: 0;
          height: 1.5px;
          background: currentColor;
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          transform: translateX(-50%);
          opacity: 0;
        }
        .nav-link:hover::after {
          width: 60%;
          opacity: 0.5;
        }
        .nav-link:hover {
          transform: translateY(-1px);
        }
        .nav-link:active {
          transform: translateY(0);
        }
      `}</style>
      <nav
        className="fixed top-0 left-0 right-0 h-14 flex items-center justify-between px-6 z-50"
        style={{
          background: `rgba(var(--rgb-cream-light), ${progress * 0.95})`,
          backdropFilter: progress > 0.1 ? `blur(${progress * 12}px)` : 'none',
          WebkitBackdropFilter:
            progress > 0.1 ? `blur(${progress * 12}px)` : 'none',
          borderBottom: `1px solid rgba(var(--rgb-black), ${progress * 0.06})`,
          transition: 'none', // Using scroll-driven values, no CSS transition needed
        }}
      >
        {/* Left: Logo */}
        <div className="flex items-center gap-8">
          <Link to="/" className="no-underline flex items-center gap-2">
            <Logo size={22} variant={progress < 0.5 ? 'light' : 'default'} />
            <span
              className="font-serif text-lg font-medium"
              style={{
                color:
                  progress < 0.5 ? 'var(--color-white)' : 'var(--color-ink)',
                transition: 'color 0.2s ease',
              }}
            >
              Spreadsheet Arena
            </span>
          </Link>
        </div>

        {/* Right: Links */}
        <div className="flex items-center gap-1">
          <Link
            to="/leaderboard"
            className="nav-link no-underline text-[13px] font-medium px-3 py-1.5 rounded-md transition-all duration-200"
            style={linkStyle('/leaderboard')}
          >
            Leaderboard
          </Link>
          {isMeridianUser && (
            <Link
              to="/community-votes"
              className="nav-link no-underline text-[13px] font-medium px-3 py-1.5 rounded-md transition-all duration-200"
              style={linkStyle('/community-votes')}
            >
              Community Votes
            </Link>
          )}

          {user && (
            <>
              <div
                className="w-px h-4"
                style={{
                  background:
                    progress < 0.5
                      ? 'rgba(var(--rgb-white), 0.2)'
                      : 'rgba(var(--rgb-black), 0.1)',
                  transition: 'background 0.2s ease',
                }}
              />
              <span
                className="text-[13px] hidden lg:block"
                style={{
                  color:
                    progress < 0.5
                      ? 'rgba(var(--rgb-white), 0.5)'
                      : 'var(--color-gray-light)',
                  transition: 'color 0.2s ease',
                }}
              >
                {user.email}
              </span>
              <button
                onClick={handleSignOut}
                className="text-[13px] font-medium"
                style={{
                  background: 'none',
                  border: 'none',
                  color:
                    progress < 0.5
                      ? 'rgba(var(--rgb-white), 0.7)'
                      : 'var(--color-gray)',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'color 0.2s ease',
                }}
              >
                Sign Out
              </button>
            </>
          )}
        </div>
      </nav>
    </>
  );
}
